import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import Cookies from 'js-cookie';
import { Card, Header, Title, Subtitle, HeaderLeft, Body, Lock, DropOverlay, DropOverlayText, } from './styles';
import { LockIcon } from 'src/assets/Images';
import { ticketsService } from 'src/Utils/ticketsService';
import { adminUsersService } from 'src/Utils/adminUsersService';
import ArrangementRow from 'src/Components/Tickets/Arrangements/ArrangementRow';
import KebabMenu from './KebabMenu';
import AuthorizePaymentModal from './AuthorizePaymentModal';
import ProviderModal from './ProviderModal';
import { useCreateArrangement, useInternalConversationActions } from './hooks';
import { ConversationInput, useConversationComposer } from '../shared';
import { buildAdminMentionableUsers } from '../shared/mentionableUsers';
import { deriveBudgetRequestState } from './helpers/deriveBudgetRequestState';
import { COMPLETED, TYPE_CONTACT } from 'src/Utils/constants';
var InternalConversation = function (_a) {
    var ticket = _a.ticket, fullWidth = _a.fullWidth, children = _a.children, onSend = _a.onSend, onActionSelect = _a.onActionSelect;
    var isTicketCompleted = ticket.status === COMPLETED;
    var isContactUser = Cookies.get('User') === TYPE_CONTACT;
    var _b = useQuery(['ticket-arrangements', ticket.id], function () { return ticketsService.getTicketArrangements(ticket.id); }, {
        enabled: !!(ticket === null || ticket === void 0 ? void 0 : ticket.id),
    }), arrangementsData = _b.data, refetchArrangements = _b.refetch;
    var _c = useQuery('mentionable-admin-users-internal-conversation', adminUsersService.getAssignableUsers, {
        enabled: !isContactUser,
    }).data, mentionableAdminUsers = _c === void 0 ? [] : _c;
    var adminMentionableUsers = useMemo(function () { return buildAdminMentionableUsers(mentionableAdminUsers); }, [mentionableAdminUsers]);
    var arrangementRows = useMemo(function () {
        if (!arrangementsData || !arrangementsData.arrangements) {
            return [];
        }
        var apiArrangements = arrangementsData.arrangements;
        return apiArrangements.map(function (a) {
            var _a, _b, _c, _d;
            return ({
                id: a.id,
                arrangement_type: a.arrangement_type,
                provider_name: (_b = (_a = a.provider) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null,
                authorized_by_name: ((_c = a.authorized_by) === null || _c === void 0 ? void 0 : _c.name) || null,
                created_by_name: ((_d = a.created_by) === null || _d === void 0 ? void 0 : _d.name) || null,
                created_at: a.created_at,
                internal_comment: a.internal_comment || null,
                files_count: Array.isArray(a.files) ? a.files.length : 0,
                files: Array.isArray(a.files)
                    ? a.files.map(function (f) { return ({ id: f.id, name: f.name, url: f.url }); })
                    : [],
            });
        });
    }, [arrangementsData]);
    var _d = useMemo(function () { return deriveBudgetRequestState(arrangementsData); }, [arrangementsData]), budgetRequests = _d.budgetRequests, pendingBudgetRequests = _d.pendingBudgetRequests;
    var _e = useConversationComposer({
        isDisabled: false,
        scrollDependency: arrangementsData,
    }), text = _e.text, setText = _e.setText, mentionedAdminUserIds = _e.mentionedAdminUserIds, setMentionedAdminUserIds = _e.setMentionedAdminUserIds, bodyRef = _e.bodyRef, resetComposer = _e.resetComposer, files = _e.files, filePreviews = _e.filePreviews, fileInputRef = _e.fileInputRef, handleFileSelect = _e.handleFileSelect, handleAttachClick = _e.handleAttachClick, handleRemoveFile = _e.handleRemoveFile, formatFileSize = _e.formatFileSize, isDragging = _e.isDragging, handleDragEnter = _e.handleDragEnter, handleDragLeave = _e.handleDragLeave, handleDragOver = _e.handleDragOver, handleDrop = _e.handleDrop;
    var validMentionLabels = adminMentionableUsers.map(function (mentionableUser) { return mentionableUser.fullName; });
    var handleSend = function () {
        mutateCreateArrangement({
            arrangementType: 'comment',
            internalComment: text.trim(),
            files: files,
            mentionedAdminUserIds: mentionedAdminUserIds,
        });
        resetComposer();
    };
    var _f = useCreateArrangement({
        ticketId: ticket.id,
        onSuccess: function (arrangementType) {
            refetchArrangements();
            if (arrangementType !== 'comment') {
                closeModal();
            }
        },
    }), mutateCreateArrangement = _f.mutateCreateArrangement, isCreatingArrangement = _f.isCreatingArrangement;
    var _g = useInternalConversationActions({
        budgetRequestsCount: budgetRequests.length,
        pendingBudgetRequestsCount: pendingBudgetRequests.length,
        onActionSelect: onActionSelect,
    }), openModal = _g.openModal, handleActionSelect = _g.handleActionSelect, closeModal = _g.closeModal, isProviderModalOpen = _g.isProviderModalOpen, providerModalType = _g.providerModalType;
    var handleAuthorizePaymentConfirm = function (authorizedById, budgetRequestId, files) {
        var payload = {
            arrangementType: 'payment_authorization',
            authorizedById: authorizedById,
            budgetRequestId: budgetRequestId,
            files: files,
        };
        mutateCreateArrangement(payload);
    };
    var handleProviderModalConfirm = function (arrangementType, providerId, internalComment, files) {
        mutateCreateArrangement({
            arrangementType: arrangementType,
            providerId: providerId,
            internalComment: internalComment,
            files: files,
        });
    };
    return (React.createElement(Card, { fullWidth: fullWidth, "data-conversation": "internal", onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop },
        isDragging && (React.createElement(DropOverlay, null,
            React.createElement(DropOverlayText, null, "Soltar para adjuntar"))),
        React.createElement(Header, null,
            React.createElement(HeaderLeft, null,
                React.createElement(Title, null, "Conversaci\u00F3n Interna"),
                React.createElement(Subtitle, null,
                    React.createElement(Lock, { src: LockIcon, alt: "Privado" }),
                    "Esta informaci\u00F3n no ser\u00E1 compartida con el cliente."))),
        React.createElement(Body, { ref: bodyRef },
            children,
            Array.isArray(arrangementRows) &&
                arrangementRows.map(function (row) { return (React.createElement(ArrangementRow, { key: row.id, data: row, validMentions: validMentionLabels })); })),
        React.createElement(ConversationInput, { text: text, onTextChange: setText, onSend: handleSend, files: files, filePreviews: filePreviews, fileInputRef: fileInputRef, handleFileSelect: handleFileSelect, handleAttachClick: handleAttachClick, handleRemoveFile: handleRemoveFile, formatFileSize: formatFileSize, isLoading: isCreatingArrangement, disabled: false, extraActions: !isTicketCompleted && React.createElement(KebabMenu, { onSelect: handleActionSelect }), footerLayout: "flex", mentionableUsers: adminMentionableUsers, onMentionedAdminUserIdsChange: setMentionedAdminUserIds }),
        React.createElement(AuthorizePaymentModal, { isOpen: openModal === 'authorize_payment', onClose: closeModal, ticket: ticket, budgetRequests: pendingBudgetRequests, onConfirm: handleAuthorizePaymentConfirm, isLoading: isCreatingArrangement }),
        isProviderModalOpen && (React.createElement(ProviderModal, { isOpen: true, onClose: closeModal, ticket: ticket, type: providerModalType, onConfirm: function (providerId, internalComment, files) {
                return handleProviderModalConfirm(providerModalType, providerId, internalComment, files);
            }, isLoading: isCreatingArrangement }))));
};
export default InternalConversation;
